import type { Address } from '@this+that/models';
import log from 'loglevel';

import { type DraftData, generateDraftSaveId } from './useSmartDraftManager';
import { ComposeSnapshotStatus } from '../../../../../enums/composeSnapshotStatus';
import type { FileAttachment } from '../../../../../hooks/useFileAttachments';
import type { ComposeSnapshot } from '../../../../../models/composeSnapshot';
import { fileAttachmentFromBase64 } from '../../../../../utils/fileAttachmentFromBase64';

export interface RestorableContent {
  subject: string;
  message: string;
  modifiedQuotedContent: string | null;
  toAddresses: Address[];
  ccAddresses: Address[];
  bccAddresses: Address[];
  fromEmail?: string;
  attachments: FileAttachment[];
  attachmentsToRemove?: string[];
}

export enum RestoreDecision {
  RESTORE = 'restore',
  DROP_EQUAL = 'dropEqual',
  SKIP = 'skip',
}

export interface BuiltRestoredContent {
  content: RestorableContent;
  missingAttachmentNames: string[];
}

export const toRestorableContent = (data: DraftData): RestorableContent => ({
  subject: data.subject ?? '',
  message: data.message ?? '',
  modifiedQuotedContent: data.modifiedQuotedContent ?? null,
  toAddresses: data.toAddresses,
  ccAddresses: data.ccAddresses,
  bccAddresses: data.bccAddresses,
  fromEmail: data.fromEmail,
  attachments: data.attachments ?? [],
});

export const toDraftData = (
  content: RestorableContent,
  base: DraftData,
): DraftData => ({
  ...base,
  subject: content.subject,
  message: content.message,
  modifiedQuotedContent: content.modifiedQuotedContent,
  toAddresses: content.toAddresses,
  ccAddresses: content.ccAddresses,
  bccAddresses: content.bccAddresses,
  fromEmail: content.fromEmail,
  attachments: content.attachments,
});

interface RebuiltAttachments {
  attachments: FileAttachment[];
  failedNames: string[];
}

export const rebuildAttachments = (
  snapshotAttachments: ComposeSnapshot['attachments'],
): RebuiltAttachments => {
  const result: RebuiltAttachments = { attachments: [], failedNames: [] };
  for (const { name, contentType, contentBytes } of snapshotAttachments) {
    if (!contentBytes) {
      result.failedNames.push(name);
      continue;
    }
    try {
      result.attachments.push(
        fileAttachmentFromBase64({ name, contentType, contentBytes }),
      );
    } catch (error) {
      log.warn('Compose snapshot attachment could not be rebuilt', error);
      result.failedNames.push(name);
    }
  }
  return result;
};

// A truncated snapshot carries no bytes: keep the attachments the form already has.
export const buildRestoredContent = (
  snapshot: ComposeSnapshot,
  current: RestorableContent,
  isFromLocked: boolean,
): BuiltRestoredContent => {
  const userRemoved = new Set(snapshot.attachmentsToRemove);
  const rebuilt = snapshot.attachmentsTruncated
    ? {
        attachments: current.attachments.filter(
          ({ name }) => !userRemoved.has(name),
        ),
        failedNames: [] as string[],
      }
    : rebuildAttachments(snapshot.attachments);
  const currentNames = new Set(current.attachments.map(({ name }) => name));
  const missingAttachmentNames = snapshot.attachmentsTruncated
    ? snapshot.attachments
        .map(({ name }) => name)
        .filter((name) => !currentNames.has(name))
    : rebuilt.failedNames;

  return {
    missingAttachmentNames,
    content: {
      subject: snapshot.subject,
      message: snapshot.message,
      modifiedQuotedContent: snapshot.modifiedQuotedContent ?? null,
      toAddresses: snapshot.toAddresses,
      ccAddresses: snapshot.ccAddresses,
      bccAddresses: snapshot.bccAddresses,
      fromEmail: isFromLocked
        ? current.fromEmail
        : (snapshot.fromEmail ?? current.fromEmail),
      attachments: rebuilt.attachments,
      attachmentsToRemove: snapshot.attachmentsToRemove,
    },
  };
};

interface DecideRestoreInput {
  snapshot: ComposeSnapshot | undefined;
  ownSessionId: string;
  restored: RestorableContent;
  missingAttachmentNames: string[];
  currentData: DraftData;
}

export const decideRestore = ({
  snapshot,
  ownSessionId,
  restored,
  missingAttachmentNames,
  currentData,
}: DecideRestoreInput): RestoreDecision => {
  if (
    !snapshot ||
    snapshot.sessionId === ownSessionId ||
    snapshot.status !== ComposeSnapshotStatus.DIRTY
  ) {
    return RestoreDecision.SKIP;
  }
  // Attachment names are invisible to the save id: only the pill can tell the user what to re-attach.
  if (snapshot.attachmentsTruncated && missingAttachmentNames.length > 0) {
    return RestoreDecision.RESTORE;
  }
  if (
    generateDraftSaveId(toDraftData(restored, currentData)) ===
    generateDraftSaveId(currentData)
  ) {
    return RestoreDecision.DROP_EQUAL;
  }
  return RestoreDecision.RESTORE;
};
